import { setInteractionModality } from "@react-aria/interactions";
import { useTypeSelect } from "@react-aria/selection";
import { filterDOMProps, focusWithoutScrolling } from "@react-aria/utils";
import { useMemo } from "react";
import {
  chain,
  ListKeyboardDelegate,
  mergeProps,
  useCollator,
  useField,
  useId,
  useMenuTrigger,
} from "react-aria";

import type { FocusableElement } from "@react-types/shared";
import type { FocusEvent, HTMLAttributes, RefObject } from "react";
import type { AriaButtonProps, AriaListBoxOptions, PressEvent } from "react-aria";
import type { MultiSelectProps, MultiSelectState } from "./useMultiSelectState";

export interface MultiSelectAria<T> {
  /** Props for the label element. */
  labelProps: HTMLAttributes<HTMLElement>;
  /** Props for the popup trigger element. */
  triggerProps: AriaButtonProps;
  /** Props for the element representing the selected value. */
  valueProps: HTMLAttributes<HTMLElement>;
  /** Props for the popup. */
  menuProps: AriaListBoxOptions<T>;
  /** Props for the select's description element, if any. */
  descriptionProps: HTMLAttributes<HTMLElement>;
  /** Props for the select's error message element, if any. */
  errorMessageProps: HTMLAttributes<HTMLElement>;
}

export function useMultiSelect<T>(
  props: MultiSelectProps<T>,
  state: MultiSelectState<T>,
  ref: RefObject<HTMLElement>
): MultiSelectAria<T> {
  const { disallowEmptySelection, isDisabled } = props;

  const collator = useCollator({ usage: "search", sensitivity: "base" });
  const delegate = useMemo(
    () => new ListKeyboardDelegate(state.collection, state.disabledKeys, null as never, collator),
    [state.collection, state.disabledKeys, collator]
  );

  const { menuTriggerProps, menuProps } = useMenuTrigger<T>(
    {
      isDisabled,
      type: "listbox",
    },
    state,
    ref
  );

  const triggerOnKeyDown = (e: KeyboardEvent) => {
    // Select items when trigger has focus - imitating default `<select>` behavior.
    // In multi selection mode it does not make sense.
    if (state.selectionMode === "single") {
      switch (e.key) {
        case "ArrowLeft": {
          // prevent scrolling containers
          e.preventDefault();

          const key =
            state.selectedKeys.size > 0
              ? delegate.getKeyAbove(state.selectedKeys.values().next().value!)
              : delegate.getFirstKey();

          if (key) {
            state.setSelectedKeys([key]);
          }
          break;
        }
        case "ArrowRight": {
          // prevent scrolling containers
          e.preventDefault();

          const key =
            state.selectedKeys.size > 0
              ? delegate.getKeyBelow(state.selectedKeys.values().next().value!)
              : delegate.getFirstKey();

          if (key) {
            state.setSelectedKeys([key]);
          }
          break;
        }

        // no default
      }
    }
  };

  // Typeahead functionality - imitating default `<select>` behavior.
  const { typeSelectProps } = useTypeSelect({
    keyboardDelegate: delegate,
    selectionManager: state.selectionManager,
    onTypeSelect(key) {
      state.setSelectedKeys([key]);
    },
  });

  const { labelProps, fieldProps, descriptionProps, errorMessageProps } = useField({
    ...props,
    labelElementType: "span",
  });

  typeSelectProps.onKeyDown = typeSelectProps.onKeyDownCapture;
  delete typeSelectProps.onKeyDownCapture;

  const domProps = filterDOMProps(props, { labelable: true });
  const triggerProps = mergeProps(typeSelectProps, menuTriggerProps, fieldProps);

  const valueId = useId();

  return {
    labelProps: {
      ...labelProps,
      onClick: () => {
        if (!props.isDisabled) {
          ref.current?.focus();

          // Show the focus ring so the user knows where focus went
          setInteractionModality("keyboard");
        }
      },
    },
    triggerProps: mergeProps(domProps, {
      ...triggerProps,
      // the onPressStart callback returned by useMenuTrigger doesn't appear to be toggling the menu
      // state correctly - it doesn't close on click when the menu is open
      onPressStart: (e: PressEvent) => {
        if (!isDisabled) {
          focusWithoutScrolling(e.target as FocusableElement);
          state.toggle();
        }
      },
      onKeyDown: chain(triggerProps.onKeyDown, triggerOnKeyDown, props.onKeyDown),
      onKeyUp: props.onKeyUp,
      "aria-labelledby": [
        valueId,
        domProps["aria-label"] !== undefined
          ? domProps["aria-labelledby"] !== undefined
            ? domProps["aria-labelledby"]
            : triggerProps.id
          : triggerProps["aria-labelledby"],
      ].join(" "),
      onFocus(e: FocusEvent) {
        if (state.isFocused) {
          return;
        }

        if (props.onFocus) {
          props.onFocus(e);
        }

        state.setFocused(true);
      },
      onBlur(e: FocusEvent) {
        if (state.isOpen) {
          return;
        }

        if (props.onBlur) {
          props.onBlur(e);
        }

        state.setFocused(false);
      },
    }),
    valueProps: {
      id: valueId,
    },
    menuProps: {
      ...menuProps,
      disallowEmptySelection,
      autoFocus: state.focusStrategy || true,
      shouldSelectOnPressUp: true,
      shouldFocusOnHover: true,
      onBlur: e => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) {
          return;
        }

        if (props.onBlur) {
          props.onBlur(e);
        }
        state.setFocused(false);
      },
      // @ts-expect-error: `onFocus` is not part of `AriaMenuOptions`
      onFocus: menuProps?.onFocus,
      "aria-labelledby": [
        fieldProps["aria-labelledby"],
        triggerProps["aria-label"] && !fieldProps["aria-labelledby"] ? triggerProps.id : null,
      ]
        .filter(Boolean)
        .join(" "),
    },
    descriptionProps,
    errorMessageProps,
  };
}
